<template>
  <div>
    <!-- TABLE -->
    <div class="inv-table-wrap">
      <table class="inv-table">
        <thead>
          <tr>
            <th class="inv-th inv-th--id">#</th>
            <th
              v-for="h in visibleHeaders"
              :key="h.value"
              class="inv-th"
              :class="{ 'inv-th--sortable': h.sortable !== false, 'inv-th--active': sortBy === h.value }"
              @click="h.sortable !== false && $emit('sort', h.value)"
            >
              {{ h.text }}
              <span v-if="h.sortable !== false" class="inv-th__arrow">
                {{ sortBy === h.value ? (sortDesc ? '↓' : '↑') : '↕' }}
              </span>
            </th>
            <th class="inv-th inv-th--actions">OPS</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="visibleHeaders.length + 2" class="inv-td inv-td--loading">
              <span class="inv-loading-bar"></span>
            </td>
          </tr>
          <tr v-else-if="items.length === 0">
            <td :colspan="visibleHeaders.length + 2" class="inv-td inv-td--empty">
              <div class="inv-empty-inner">
                <span class="inv-empty-icon">◻</span>
                <span>NO RESULTS FOUND</span>
              </div>
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="item in items"
              :key="item.id"
              class="inv-tr"
              :class="'inv-tr--status-' + item.status"
              @click="$emit('view', item)"
            >
              <td class="inv-td inv-td--id">{{ item.id }}</td>

              <td class="inv-td inv-td--name" data-label="NAME">
                <div class="inv-name">
                  <span class="inv-name__icon" :style="{ color: typeHex(item.type) }">{{ typeGlyph(item.type) }}</span>
                  <div>
                    <div class="inv-name__main">{{ item.name }}</div>
                    <div v-if="item.model" class="inv-name__sub">{{ item.model }}</div>
                  </div>
                </div>
              </td>

              <td class="inv-td inv-td--mono" data-label="SERIAL">
                <span v-if="item.serial_number" class="inv-serial">{{ item.serial_number }}</span>
                <span v-else class="inv-null">NULL</span>
              </td>

              <td class="inv-td" data-label="TYPE">
                <span class="inv-badge" :style="{ '--badge-color': typeHex(item.type) }">
                  {{ typeName(item.type) }}
                </span>
              </td>

              <td class="inv-td" data-label="STATUS">
                <span class="inv-status" :class="'inv-status--' + item.status">
                  <span class="inv-status__dot"></span>
                  {{ statusName(item.status) }}
                </span>
              </td>

              <td class="inv-td inv-td--date" data-label="PURCHASE">{{ formatDate(item.purchase_date) }}</td>

              <td class="inv-td inv-td--warranty" data-label="WARRANTY">
                <span v-if="item.warranty_expires_at" class="inv-warranty" :class="warrantyClass(item.warranty_expires_at)">
                  {{ formatDate(item.warranty_expires_at) }}
                </span>
                <span v-else class="inv-null">—</span>
              </td>

              <td class="inv-td inv-td--touchpr" data-label="TOUCH">
                <span v-if="item.touch_protection_date" class="inv-touchpr" :class="warrantyClass(item.touch_protection_date)">
                  {{ formatDate(item.touch_protection_date) }}
                </span>
                <span v-else class="inv-null">—</span>
              </td>

              <td class="inv-td inv-td--user" data-label="USER">
                <span v-if="item.user" class="inv-user">
                  {{ formatUser(item.user) }}
                </span>
                <span v-else class="inv-null">—</span>
              </td>

              <td class="inv-td inv-td--notes" data-label="NOTES" @click.stop>
                <v-tooltip bottom v-if="item.notes">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" v-bind="attrs" v-on="on">mdi-note-outline</v-icon>
                  </template>
                  <span>{{ item.notes }}</span>
                </v-tooltip>
                <span v-else class="inv-null">—</span>
              </td>

              <td class="inv-td inv-td--actions" @click.stop>
                <button class="inv-action inv-action--edit" title="Edit" @click="$emit('edit', item)">✎</button>
                <button class="inv-action inv-action--delete" title="Delete" @click="$emit('delete', item)">✕</button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- PAGINATION -->
    <div class="inv-pagination">
      <div class="inv-pagination__info">
        Pages {{ currentPage }} / {{ totalPages }} &nbsp;·&nbsp;
        Rows per page:
        <span
          v-for="n in [5, 10, 15, 25, 50]"
          :key="n"
          class="inv-pagination__size"
          :class="{ 'inv-pagination__size--active': perPage === n }"
          @click="$emit('update:perPage', n); $emit('update:currentPage', 1)"
        >{{ n }}</span>
      </div>
      <div class="inv-pagination__nav">
        <button class="inv-pgbtn" :disabled="currentPage === 1" @click="$emit('update:currentPage', currentPage - 1)">←</button>
        <button
          v-for="p in pageRange"
          :key="p"
          class="inv-pgbtn"
          :class="{ 'inv-pgbtn--active': p === currentPage }"
          @click="$emit('update:currentPage', p)"
        >{{ p }}</button>
        <button class="inv-pgbtn" :disabled="currentPage === totalPages" @click="$emit('update:currentPage', currentPage + 1)">→</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ToolsTable',
  props: {
    items:         { type: Array,   required: true },
    loading:       { type: Boolean, default: false },
    visibleHeaders:{ type: Array,   required: true },
    sortBy:        { type: String,  default: 'id' },
    sortDesc:      { type: Boolean, default: true },
    currentPage:   { type: Number,  default: 1 },
    perPage:       { type: Number,  default: 15 },
    totalPages:    { type: Number,  default: 1 },
    pageRange:     { type: Array,   default: () => [] },
    typeOptions:   { type: Array,   required: true },
    statusOptions: { type: Array,   required: true },
  },
  methods: {
    typeName(v)   { return (this.typeOptions.find(o => o.value === v) || {}).text || '—' },
    statusName(v) { return (this.statusOptions.find(o => o.value === v) || {}).text || '—' },
    typeHex(v)    { return { 1: '#f59e0b', 2: '#3b82f6' }[v] || '#94a3b8' },
    typeGlyph(v)  { return { 1: '🔧', 2: '⚙️' }[v] || '◎' },
    formatUser(d) { if (!d) return '—'; const name = [d.surname, d.christian_name1, d.christian_name2].filter(Boolean).join(' '); return `${name}`; },
    warrantyClass(d) {
      const diff = (new Date(d) - new Date()) / 86400000
      if (diff < 0)  return 'inv-warranty--expired'
      if (diff < 90) return 'inv-warranty--soon'
      return 'inv-warranty--ok'
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('hu-HU')
    },
  },
}
</script>

<style scoped>
.inv-table-wrap { overflow-x: auto; }
.inv-table { width: 100%; border-collapse: collapse; font-size: 16px; }
.inv-th {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px; font-weight: 600; letter-spacing: 0.12em;
  color: var(--text-dim); padding: 10px 14px; text-align: left;
  border-bottom: 1px solid var(--border); background: var(--bg2);
  white-space: nowrap; user-select: none;
}
.inv-th--sortable { cursor: pointer; }
.inv-th--sortable:hover { color: var(--text); }
.inv-th--active   { color: var(--accent2); }
.inv-th--id       { width: 60px; color: var(--border2); }
.inv-th--actions  { width: 80px; text-align: center; }
.inv-th__arrow    { margin-left: 4px; opacity: 0.5; }

.inv-tr { border-bottom: 1px solid var(--border); cursor: pointer; transition: background 0.1s; }
.inv-tr:hover { background: var(--bg3); }
.inv-tr--status-3 { border-left: 2px solid var(--warn); }
.inv-tr--status-4 { border-left: 2px solid var(--danger); opacity: 0.6; }

.inv-td { padding: 11px 14px; vertical-align: middle; color: var(--text); }
.inv-td--id       { font-family: 'IBM Plex Mono', monospace; font-size: 14px; color: var(--border2); }
.inv-td--mono     { font-family: 'IBM Plex Mono', monospace; }
.inv-td--date     { font-family: 'IBM Plex Mono', monospace; font-size: 15px; white-space: nowrap; }
.inv-td--warranty { white-space: nowrap; }
.inv-td--actions  { text-align: center; }
.inv-td--loading, .inv-td--empty {
  text-align: center; padding: 48px; color: var(--text-dim);
  font-family: 'IBM Plex Mono', monospace; font-size: 14px; letter-spacing: 0.1em;
  width: 100%;
}
.inv-td--empty .inv-empty-inner { display: flex; flex-direction: column; align-items: center; gap: 8px; width: 100%; }

.inv-name       { display: flex; align-items: center; gap: 10px; }
.inv-name__icon { font-size: 20px; flex-shrink: 0; }
.inv-name__main { font-weight: 500; color: var(--text-bright); }
.inv-name__sub  { font-size: 14px; color: var(--text-dim); font-family: 'IBM Plex Mono', monospace; }
.inv-serial     { font-size: 14px; color: var(--text-dim); letter-spacing: 0.04em; }
.inv-null       { color: var(--border2); font-family: 'IBM Plex Mono', monospace; font-size: 14px; }
.inv-empty-icon { font-size: 40px; display: block; }

.inv-badge {
  display: inline-block; font-family: 'IBM Plex Mono', monospace;
  font-size: 13px; font-weight: 600; letter-spacing: 0.08em;
  padding: 3px 8px; border: 1px solid var(--badge-color, #94a3b8);
  color: var(--badge-color, #94a3b8);
  background: color-mix(in srgb, var(--badge-color, #94a3b8) 10%, transparent);
}

.inv-status { display: inline-flex; align-items: center; gap: 6px; font-family: 'IBM Plex Mono', monospace; font-size: 13px; font-weight: 600; letter-spacing: 0.06em; }
.inv-status__dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.inv-status--1 { color: var(--success); } .inv-status--1 .inv-status__dot { background: var(--success); }
.inv-status--2 { color: var(--text-dim); } .inv-status--2 .inv-status__dot { background: var(--text-dim); }
.inv-status--3 { color: var(--warn); }    .inv-status--3 .inv-status__dot { background: var(--warn); }
.inv-status--4 { color: var(--danger); }  .inv-status--4 .inv-status__dot { background: var(--danger); }

.inv-warranty { font-family: 'IBM Plex Mono', monospace; font-size: 14px; padding: 2px 6px; }
.inv-warranty--ok      { color: var(--success); background: rgba(34,197,94,0.08); }
.inv-warranty--soon    { color: var(--warn);    background: rgba(245,158,11,0.1); }
.inv-warranty--expired { color: var(--danger);  background: rgba(239,68,68,0.08); text-decoration: line-through; }

.inv-action {
  background: transparent; border: 1px solid var(--border); color: var(--text-dim);
  font-size: 16px; cursor: pointer; padding: 4px 8px; transition: all 0.12s;
  font-family: 'IBM Plex Mono', monospace; margin: 0 2px;
}
.inv-action--edit:hover   { border-color: var(--accent); color: var(--accent2); background: rgba(79,142,255,0.08); }
.inv-action--delete:hover { border-color: var(--danger); color: var(--danger); background: rgba(239,68,68,0.08); }

.inv-loading-bar { display: block; height: 2px; background: var(--accent); animation: inv-pulse 1.2s ease-in-out infinite; }
@keyframes inv-pulse { 0%,100% { opacity: 0.3 } 50% { opacity: 1 } }

.inv-pagination {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 28px; border-top: 1px solid var(--border);
  background: var(--bg2); flex-wrap: wrap; gap: 12px;
}
.inv-pagination__info { font-family: 'IBM Plex Mono', monospace; font-size: 14px; color: var(--text-dim); display: flex; align-items: center; gap: 8px; }
.inv-pagination__size { cursor: pointer; padding: 2px 6px; border: 1px solid transparent; transition: all 0.12s; }
.inv-pagination__size:hover { color: var(--text); }
.inv-pagination__size--active { color: var(--accent2); border-color: var(--accent); }
.inv-pagination__nav { display: flex; gap: 4px; }
.inv-pgbtn {
  font-family: 'IBM Plex Mono', monospace; font-size: 15px; font-weight: 600;
  background: var(--bg3); border: 1px solid var(--border); color: var(--text-dim);
  padding: 5px 10px; cursor: pointer; transition: all 0.12s; min-width: 36px;
}
.inv-pgbtn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent2); }
.inv-pgbtn--active { border-color: var(--accent); color: var(--accent2); background: rgba(79,142,255,0.1); }
.inv-pgbtn:disabled { opacity: 0.3; cursor: default; }

/* ── RESPONSIVE ── */
@media (max-width: 1024px) {
  /* Elrejti a kevésbé fontos oszlopokat tableten */
  .inv-td--date, .inv-th:nth-child(6),
  .inv-td--warranty, .inv-th:nth-child(7) { display: none; }
}

@media (max-width: 768px) {
  /* Tableten: soronkénti kártya nézet */
  .inv-table, .inv-table thead, .inv-table tbody,
  .inv-table tr, .inv-table th, .inv-table td { display: block; }

  .inv-table thead { display: none; }

  .inv-tr {
    border: 1px solid var(--border);
    margin: 8px 12px;
    padding: 12px;
    border-radius: 2px;
    position: relative;
  }

  .inv-td {
    padding: 4px 0;
    border: none;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .inv-td::before {
    content: attr(data-label);
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--text-dim);
    min-width: 110px;
    flex-shrink: 0;
  }

  .inv-td--id { display: none; }

  .inv-td--actions {
    position: absolute;
    top: 10px;
    right: 10px;
    text-align: right;
    padding: 0;
    border: none;
  }
  .inv-td--actions::before { display: none; }

  .inv-td--name { padding-right: 80px; }
  .inv-td--date, .inv-td--warranty { display: flex; }

  .inv-pagination {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 16px;
    gap: 10px;
  }
  .inv-pagination__info { flex-wrap: wrap; }
}

@media (max-width: 480px) {
  .inv-table-wrap { padding: 0; }
  .inv-tr { margin: 6px 8px; }
  .inv-pgbtn { font-size: 13px; padding: 4px 8px; min-width: 30px; }
  .inv-pagination__info { font-size: 12px; }
}
</style>